import React, { createContext, useContext, useState, ReactNode } from "react";
import { LanguageContextType } from "../types";

interface Translations {
  [key: string]: {
    [key: string]: string;
  };
}

const translations: Translations = {
  de: {
    project_info: "Projektinformationen",
    visibility_control: "Sichtbarkeitskontrolle",
    name: "Name",
    description: "Beschreibung",
    step: "Schritt",
    created: "Erstellt",
    modified: "Geändert",
    all_groups: "Alle Gruppen",
    opacity: "Transparenz",
    // Model types
    bone_defect: "Knochendefekt",
    bone_after: "Knochen nach",
    guide: "Führung",
    pin: "K-Drähte",
    implant: "Implantat",
    screws: "Schrauben",
    other: "Andere",
    loading_3d: "3D-Modell wird geladen...",
    no_models_selected: "Keine Modelle ausgewählt",
    models_visible: "Modell(e) sichtbar",
    // Steps
    preliminary_model: "Vorläufiges Modell",
    consultation: "Beratung",
    model_finalization: "Modellfinalisierung",
    final_approval: "Endgültige Genehmigung",
    manufacturing: "Herstellung",
    delivery: "Lieferung",
    // Tools
    draw: "Zeichnen",
    clear_drawing: "Zeichnung löschen",
    take_screenshot: "Screenshot",
    lighting: "Beleuchtung",
    light_studio: "Studio",
    light_top: "Von oben",
    light_side: "Von der Seite",
    light_intensity: "Lichtintensität",
    toggle_sidebar: "Seitenleiste ein-/ausblenden",
    toggle_theme: "Design wechseln",
    // Progress
    step_word: "Schritt",
    of: "von",
    // Empty state
    select_models_help:
      "Wählen Sie Modelle aus der Seitenleiste aus, um sie hier anzuzeigen",
    // Color picker
    drawing_color: "Zeichenfarbe",
    loading: "3D-Modelle werden geladen...",
  },
  en: {
    project_info: "Project Information",
    visibility_control: "Visibility Control",
    name: "Name",
    description: "Description",
    step: "Step",
    created: "Created",
    modified: "Modified",
    all_groups: "All Groups",
    opacity: "Opacity",
    // Model types
    bone_defect: "Bone Defect",
    bone_after: "Bone After",
    guide: "Guide",
    pin: "K-wires",
    implant: "Implant",
    screws: "Screws",
    other: "Other",
    loading_3d: "3D Model is loading...",
    no_models_selected: "No models selected",
    models_visible: "model(s) visible",
    // Steps
    preliminary_model: "Preliminary Model",
    consultation: "Consultation",
    model_finalization: "Model Finalization",
    final_approval: "Final Approval",
    manufacturing: "Manufacturing",
    delivery: "Delivery",
    // Tools
    draw: "Draw",
    clear_drawing: "Clear Drawing",
    take_screenshot: "Screenshot",
    lighting: "Lighting",
    light_studio: "Studio",
    light_top: "From above",
    light_side: "From the side",
    light_intensity: "Light intensity",
    toggle_sidebar: "Toggle Sidebar",
    toggle_theme: "Toggle Theme",
    // Progress
    step_word: "Step",
    of: "of",
    // Empty state
    select_models_help: "Select models from the sidebar to view them here",
    // Color picker
    drawing_color: "Drawing Color",
    loading: "Loading 3D models...",
  },
  ua: {
    project_info: "Інформація про проект",
    visibility_control: "Контроль видимості",
    name: "Назва",
    description: "Опис",
    step: "Стадія",
    created: "Створено",
    modified: "Змінено",
    all_groups: "Всі групи",
    opacity: "Прозорість",
    // Model types
    bone_defect: "Дефект кістки",
    bone_after: "Кістка після",
    guide: "Хірургічна направляюча",
    pin: "Cпиці Кіршнера",
    implant: "Імплант",
    screws: "Гвинти",
    other: "Інше",
    loading_3d: "3D-модель завантажується...",
    no_models_selected: "Не обрано моделей",
    models_visible: "модель(і) видимі",
    // Steps
    preliminary_model: "Попередня модель",
    consultation: "Консультація",
    model_finalization: "Фіналізація моделі",
    final_approval: "Остаточне затвердження",
    manufacturing: "Виробництво",
    delivery: "Доставка",
    // Tools
    draw: "Малювати",
    clear_drawing: "Очистити малюнок",
    take_screenshot: "Знімок екрана",
    lighting: "Освітлення",
    light_studio: "Студія",
    light_top: "Зверху",
    light_side: "Збоку",
    light_intensity: "Інтенсивність світла",
    toggle_sidebar: "Перемкнути бічну панель",
    toggle_theme: "Перемкнути тему",
    // Progress
    step_word: "Стадія",
    of: "з",
    // Empty state
    select_models_help:
      "Виберіть моделі з бічної панелі, щоб переглянути їх тут",
    // Color picker
    drawing_color: "Колір малювання",
    loading: "Завантаження 3D моделей...",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const savedLanguage = localStorage.getItem("selectedLanguage");
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    savedLanguage || "en"
  );
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(
    Object.keys(translations)
  );

  const updateAvailableLanguages = (configLanguages?: string[]) => {
    if (configLanguages && configLanguages.length > 0) {
      const validLanguages = configLanguages.filter(
        (lang) => translations[lang]
      );

      if (validLanguages.length > 0) {
        setAvailableLanguages(validLanguages);

        if (!validLanguages.includes(currentLanguage)) {
          const newLanguage = validLanguages[0];
          setCurrentLanguage(newLanguage);
          localStorage.setItem("selectedLanguage", newLanguage);
        }
      }
    } else {
      setAvailableLanguages(Object.keys(translations));
    }
  };

  const t = (key: string): string => {
    return translations[currentLanguage]?.[key] || key;
  };

  const changeLanguage = (language: string): void => {
    if (availableLanguages.includes(language)) {
      setCurrentLanguage(language);
      localStorage.setItem("selectedLanguage", language);
    }
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        changeLanguage,
        t,
        availableLanguages,
        updateAvailableLanguages,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
